import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  sementes: 'Sementes',
  defensivos: 'Defensivos',
  fertilizantes: 'Fertilizantes',
  agricultura_precisao: 'Agricultura de precisão',
};

const ProdutoDetalhes = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const categoryColors = {
    sementes: 'from-green-600 to-green-700',
    defensivos: 'from-blue-600 to-blue-700',
    fertilizantes: 'from-yellow-600 to-yellow-700',
    agricultura_precisao: 'from-purple-600 to-purple-700',
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setRelated([]);
        const response = await axios.get(`${API}/products/${slug}`);
        const current = response.data;
        setProduct(current);

        // Buscar produtos relacionados: mesma categoria, exceto o atual
        const relResp = await axios.get(`${API}/products?category=${current.category}`);
        const others = relResp.data.filter((p) => p.slug !== current.slug);
        // Priorizar a mesma classe agronômica
        const sameClass = others.filter((p) => p.classe_agronomica === current.classe_agronomica);
        const rest = others.filter((p) => p.classe_agronomica !== current.classe_agronomica);
        setRelated([...sameClass, ...rest].slice(0, 4));
      } catch (err) {
        console.error('Erro ao buscar produto:', err);
        setError('Produto não encontrado');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Produto não encontrado</h2>
          <Link to="/produtos" className="text-green-600 hover:text-green-700 font-semibold">
            ← Voltar para produtos
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className={`bg-gradient-to-r ${categoryColors[product.category] || 'from-gray-600 to-gray-700'} text-white py-12`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/produtos" className="inline-flex items-center text-white hover:text-gray-200 mb-4">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar para produtos
          </Link>
          <h1 className="text-5xl font-bold">{product.name}</h1>
        </div>
      </section>

      {/* Product Details */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Image */}
            <div className="bg-gray-100 rounded-xl overflow-hidden shadow-lg flex items-center justify-center p-8">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="max-h-96 max-w-full object-contain"
                />
              ) : (
                <div className="w-full h-96 flex items-center justify-center bg-gradient-to-br from-green-100 to-blue-100">
                  <Package className="w-32 h-32 text-gray-400" />
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Informações do Produto</h2>
              
              {product.description && (
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <Info className="w-5 h-5 mr-2" />
                    Descrição
                  </h3>
                  <p className="text-gray-700">{product.description}</p>
                </div>
              )}

              <div className="space-y-4">
                {product.classe_agronomica && (
                  <div className="border-l-4 border-green-600 pl-4">
                    <p className="text-sm text-gray-500 font-semibold">Classe Agronômica</p>
                    <p className="text-lg text-gray-800">{product.classe_agronomica}</p>
                  </div>
                )}

                {product.principio_ativo && (
                  <div className="border-l-4 border-blue-600 pl-4">
                    <p className="text-sm text-gray-500 font-semibold">Princípio Ativo</p>
                    <p className="text-lg text-gray-800">{product.principio_ativo}</p>
                  </div>
                )}

                {product.formulacao && (
                  <div className="border-l-4 border-yellow-600 pl-4">
                    <p className="text-sm text-gray-500 font-semibold">Formulação</p>
                    <p className="text-lg text-gray-800">{product.formulacao}</p>
                  </div>
                )}

                {product.grupo_quimico && (
                  <div className="border-l-4 border-purple-600 pl-4">
                    <p className="text-sm text-gray-500 font-semibold">Grupo Químico</p>
                    <p className="text-lg text-gray-800">{product.grupo_quimico}</p>
                  </div>
                )}

                {product.modo_acao && (
                  <div className="border-l-4 border-red-600 pl-4">
                    <p className="text-sm text-gray-500 font-semibold">Modo de Ação</p>
                    <p className="text-lg text-gray-800">{product.modo_acao}</p>
                  </div>
                )}

                {product.indicacoes_uso && (
                  <div className="border-l-4 border-indigo-600 pl-4">
                    <p className="text-sm text-gray-500 font-semibold">Indicações de Uso</p>
                    <p className="text-lg text-gray-800">{product.indicacoes_uso}</p>
                  </div>
                )}
              </div>

              <div className="mt-8">
                <Link
                  to="/contato"
                  className="inline-block bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-8 rounded-lg transition-colors shadow-lg"
                >
                  SOLICITAR ORÇAMENTO
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Produtos Relacionados */}
      {related.length > 0 && (
        <section className="py-12 md:py-16 bg-gray-50 border-t" data-testid="related-products-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800">Produtos relacionados</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Outros produtos em <span className="font-semibold">{CATEGORY_LABELS[product.category] || product.category}</span>
                  {product.classe_agronomica && (
                    <> — classe <span className="font-semibold">{product.classe_agronomica}</span></>
                  )}
                </p>
              </div>
              <Link
                to={`/produtos?categoria=${product.category}`}
                className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                data-testid="related-see-all"
              >
                Ver todos →
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/produtos/${p.slug}`}
                  data-testid={`related-card-${p.slug}`}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
                >
                  <div className="h-40 md:h-48 relative flex items-center justify-center p-4" style={{ backgroundColor: '#0072D6' }}>
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="max-h-full max-w-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl font-bold text-white">{p.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm md:text-base font-bold text-gray-800 line-clamp-2">{p.name}</h3>
                    {p.classe_agronomica && (
                      <p className="text-xs text-gray-500 mt-1">{p.classe_agronomica}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProdutoDetalhes;